// Pre-P2b-3 polish round 2 (Item 2): URL-search-param codecs for the
// CampaignList filter bar. Pure helpers so the round-trip rules
// (URL → state → URL) are unit-testable without React Router.
//
// Hyphenated wire forms (opt-out, group-shared, ...) keep URLs
// readable; the storage shape uses the underscore convention from
// CampaignStatus / the internal scope enum.

import type { CampaignStatus } from '../../types/campaign';
import type { DesignationFilter, ScopeFilter } from './campaignFilters';

export const URL_PARAM = {
  search: 'q',
  status: 'status',
  designation: 'designation',
  scope: 'scope',
  brand: 'brand',
} as const;

export const ALL_STATUSES: CampaignStatus[] = ['draft', 'active', 'inactive', 'archived'];
export const DEFAULT_STATUS_SELECTION: CampaignStatus[] = ['active', 'inactive'];

const URL_DESIGNATION_TO_INTERNAL: Record<string, DesignationFilter> = {
  alternate: 'alternate',
  'opt-out': 'opt_out',
  regular: 'regular',
};

export const INTERNAL_DESIGNATION_TO_URL: Record<DesignationFilter, string> = {
  all: 'all',
  alternate: 'alternate',
  opt_out: 'opt-out',
  regular: 'regular',
};

const URL_SCOPE_TO_INTERNAL: Record<string, ScopeFilter> = {
  'brand-shared': 'brand',
  'group-shared': 'group',
  'location-shared': 'location',
  'multi-shared': 'multi',
  'other-shared': 'other',
};

export const INTERNAL_SCOPE_TO_URL: Record<ScopeFilter, string> = {
  all: 'all',
  brand: 'brand-shared',
  group: 'group-shared',
  location: 'location-shared',
  multi: 'multi-shared',
  other: 'other-shared',
};

export type FilterParseResult = {
  search: string;
  status: CampaignStatus[];
  designation: DesignationFilter;
  scope: ScopeFilter;
  brand: string;
};

// Decode the URL search params into the filter state shape. Unknown /
// missing values fall back to their defaults so a malformed URL doesn't
// jam the page — every route into this component must always render
// SOMETHING.
//
// Special cases:
//   - status=none      → empty selection (the user explicitly
//                         deselected everything; preserved across
//                         reloads).
//   - status missing   → DEFAULT_STATUS_SELECTION.
//   - status=foo,bar   → filter to known CampaignStatus values; if
//                         all values are unknown, you get an empty
//                         selection, same as 'none'.
export function readFiltersFromParams(params: URLSearchParams): FilterParseResult {
  const search = params.get(URL_PARAM.search) ?? '';

  const statusRaw = params.get(URL_PARAM.status);
  let status: CampaignStatus[];
  if (statusRaw === null) {
    status = DEFAULT_STATUS_SELECTION;
  } else if (statusRaw === 'none') {
    status = [];
  } else {
    status = statusRaw
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is CampaignStatus => (ALL_STATUSES as string[]).includes(s));
  }

  const designationRaw = params.get(URL_PARAM.designation);
  const designation: DesignationFilter = designationRaw
    ? URL_DESIGNATION_TO_INTERNAL[designationRaw] ?? 'all'
    : 'all';

  const scopeRaw = params.get(URL_PARAM.scope);
  const scope: ScopeFilter = scopeRaw
    ? URL_SCOPE_TO_INTERNAL[scopeRaw] ?? 'all'
    : 'all';

  const brand = params.get(URL_PARAM.brand) ?? '';

  return { search, status, designation, scope, brand };
}

// Default-aware status comparison: same set as the default selection?
// Order doesn't matter — a multi-select toggle order shouldn't affect
// whether we omit the param.
export function statusEqualsDefault(selection: CampaignStatus[]): boolean {
  if (selection.length !== DEFAULT_STATUS_SELECTION.length) return false;
  return DEFAULT_STATUS_SELECTION.every((s) => selection.includes(s));
}

// Inverse of readFiltersFromParams. Mutates the passed URLSearchParams
// in place — the caller controls whether to push history or replace.
// Default values are omitted; only meaningful state lands in the URL.
export function writeFiltersToParams(
  params: URLSearchParams,
  state: FilterParseResult,
): void {
  for (const key of Object.values(URL_PARAM)) params.delete(key);

  const trimmed = state.search.trim();
  if (trimmed) params.set(URL_PARAM.search, trimmed);

  if (!statusEqualsDefault(state.status) && state.status.length > 0) {
    const sorted = [...state.status].sort();
    params.set(URL_PARAM.status, sorted.join(','));
  } else if (state.status.length === 0) {
    params.set(URL_PARAM.status, 'none');
  }

  if (state.designation !== 'all') {
    params.set(URL_PARAM.designation, INTERNAL_DESIGNATION_TO_URL[state.designation]);
  }
  if (state.scope !== 'all') {
    params.set(URL_PARAM.scope, INTERNAL_SCOPE_TO_URL[state.scope]);
  }
  if (state.brand) params.set(URL_PARAM.brand, state.brand);
}
